import { useState } from "react";
import { ArrowUp, ArrowDown, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { TextIn, NumIn, SelectIn } from "@/components/editor/fields";
import { shiftLos } from "@/lib/playOps";
import { api, errMsg } from "@/lib/api";

function TagsIn({ play, edit }) {
  const [text, setText] = useState(play.tags.join(", "));
  const commit = () => edit.set((p) => ({ ...p, tags: [...new Set(text.split(",").map((s) => s.trim().slice(0, 24)).filter(Boolean))].slice(0, 12) }));
  return (
    <div><label className="label">Tags (comma separated)</label>
      <input className="input" value={text} onChange={(e) => setText(e.target.value)} onBlur={commit} placeholder="Run, Red zone, Favorite" data-testid="play-tags-input" /></div>
  );
}

function mergeFormation(play, preset) {
  const sides = play.category === "defense" || play.players.some((p) => p.side === "defense") ? ["offense", "defense"] : ["offense"];
  const keep = play.players.filter((p) => !sides.includes(p.side));
  const merged = sides.flatMap((s) => preset[s].map((np) => {
    const old = play.players.find((p) => p.side === s && p.label === np.label);
    return old ? { ...old, x: np.x, y: np.y } : np;
  }));
  const players = [...keep, ...merged];
  const ids = new Set(players.map((p) => p.id));
  return {
    ...play, players,
    routes: play.routes.filter((r) => ids.has(r.player_id)),
    ball: { carrier: ids.has(play.ball.carrier) ? play.ball.carrier : null, events: play.ball.events.filter((e) => ids.has(e.from_id) && ids.has(e.to_id)) },
  };
}

export function PlayInfoPanel({ play, team, edit }) {
  const loadPreset = async () => {
    try {
      const { data } = await api.get(`/sports/football/formation`, { params: { count: team.player_count, los_y: play.los_y } });
      edit.set((p) => mergeFormation(p, data));
      toast.success("Standard formation loaded (Undo to go back)");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="play-info-panel">
      <TextIn edit={edit} label="Play name" value={play.name} maxLength={80} onValue={(p, v) => ({ ...p, name: v })} testId="play-name-input" />
      <div className="grid grid-cols-2 gap-2">
        <SelectIn label="Type" value={play.category} onChange={(v) => edit.set((p) => ({ ...p, category: v }))} options={[["offense", "Offense"], ["defense", "Defense"], ["special", "Special teams"]]} testId="play-category-select" />
        <TextIn edit={edit} label="Formation" value={play.formation} maxLength={40} onValue={(p, v) => ({ ...p, formation: v })} placeholder="e.g. Spread" testId="play-formation-input" />
      </div>
      <TagsIn play={play} edit={edit} />
      <TextIn edit={edit} area label="Description / notes" value={play.description} maxLength={500} onValue={(p, v) => ({ ...p, description: v })} testId="play-description-input" />
      <div>
        <label className="label">Line of scrimmage</label>
        <div className="flex gap-2">
          <button className="btn btn-ghost flex-1" onClick={() => edit.set((p) => (p.los_y - 5 >= 15 ? shiftLos(p, -5) : p))} data-testid="btn-los-up"><ArrowUp size={16} />5 yds</button>
          <button className="btn btn-ghost flex-1" onClick={() => edit.set((p) => (p.los_y + 5 <= 105 ? shiftLos(p, 5) : p))} data-testid="btn-los-down"><ArrowDown size={16} />5 yds</button>
        </div>
        <p className="text-xs text-slate-500 mt-1">Moves the ball spot along with all players and routes.</p>
      </div>
      <NumIn edit={edit} label="First-down marker (yards to go)" suffix="yds" step={1} min={1} max={40} value={Math.round(play.los_y - play.first_down_y)} onValue={(p, v) => ({ ...p, first_down_y: p.los_y - v })} testId="play-first-down-input" />
      <button className="btn btn-ghost w-full" onClick={loadPreset} data-testid="btn-load-preset"><LayoutGrid size={16} />Load standard {team.player_count}-player formation</button>
    </div>
  );
}
